import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '@/store';
import { decodeShareHash } from '@/lib/serialize';
import { initialShareHash } from '@/lib/initialShare';

// Loads shared characters from the URL. Two paths:
//
//   1. First page load captures `#c=<hash>` in `initialShareHash` before
//      HashRouter rewrites the URL.
//   2. During the session, if the user pastes a new `#/create/<type>?c=<hash>`
//      URL into the address bar (or is given one), re-decode and replace
//      the current character — keyed on the `c` query parameter so we don't
//      repeatedly stomp the same load.
//
// The payload is preserved as `?c=<hash>` after the route so the URL bar
// stays directly re-shareable. Copy Share Link always exports CURRENT state.
export function SharedCharacterGate({ children }: { children: ReactNode }) {
  const addCharacter = useStore((s) => s.addCharacter);
  const navigate = useNavigate();
  const location = useLocation();
  const lastHash = useRef<string | null>(null);

  useEffect(() => {
    // Determine the share hash for THIS location, in order of precedence.
    let hash: string | null = null;
    if (initialShareHash && lastHash.current === null) {
      hash = initialShareHash;
    } else if (location.search) {
      const params = new URLSearchParams(location.search);
      hash = params.get('c');
    }
    if (!hash || hash === lastHash.current) return;

    try {
      const shared = decodeShareHash(hash);
      // Add to library as a fresh entry — don't overwrite existing work.
      addCharacter(shared);
      lastHash.current = hash;
      const target = `/create/${shared.type}?c=${hash}`;
      if (location.pathname !== `/create/${shared.type}` || location.search !== `?c=${hash}`) {
        navigate(target, { replace: true });
      }
    } catch (err) {
      console.warn('share hash decode failed:', err);
    }
  }, [location.pathname, location.search, addCharacter, navigate]);

  return <>{children}</>;
}
